import { Suspense, lazy, useEffect, useState } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { api } from './lib/api';
import type { Me } from '@shared/types';
import { CommandPalette } from './components/CommandPalette';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Board from './pages/Board';
import Feed from './pages/Feed';
import Approvals from './pages/Approvals';
import Agents from './pages/Agents';
import AgentDetail from './pages/AgentDetail';
import Knowledge from './pages/Knowledge';
import Reports from './pages/Reports';
import Settings from './pages/Settings';
import HowItWorks from './pages/HowItWorks';
import BrainHub from './pages/BrainHub';

// The 3D node room pulls in the whole three.js stack (~1MB). Load it on
// demand so the pages founders open every day — Dashboard, Feed, Approvals,
// and now the Brain Hub — don't ship a 3D engine they never touch there.
// It lives behind the hub's "✦ Node view" button at /brains/:agent/nodes.
const BrainExplorer = lazy(() => import('./pages/BrainExplorer'));

export default function App() {
  const [me, setMe] = useState<Me | null | undefined>(undefined); // undefined = loading
  const loc = useLocation();

  useEffect(() => {
    api.get<Me>('/auth/me').then(setMe).catch(() => setMe(null));
  }, []);

  // If a session dies mid-use (30-day token expires, etc.), api.ts fires this;
  // dropping `me` to null bounces the founder to a clean /login instead of a
  // silently broken app they can't recover from.
  useEffect(() => {
    const onExpired = () => setMe(null);
    window.addEventListener('hq:session-expired', onExpired);
    return () => window.removeEventListener('hq:session-expired', onExpired);
  }, []);

  if (me === undefined) {
    return (
      <div className="content">
        <p className="small">Loading…</p>
      </div>
    );
  }
  if (loc.pathname === '/login') {
    if (me) return <Navigate to="/" replace />; // authed → leave the login screen
    return <Login onAuthed={() => api.get<Me>('/auth/me').then(setMe).catch(() => setMe(null))} />;
  }
  if (!me) return <Navigate to="/login" replace />;

  return (
    <>
      <CommandPalette />
      <Routes>
        <Route path="/" element={<Dashboard />} />
        <Route path="/board" element={<Board />} />
        <Route path="/feed" element={<Feed />} />
        <Route path="/approvals" element={<Approvals />} />
        <Route path="/agents" element={<Agents />} />
        <Route path="/agents/:id" element={<AgentDetail />} />
        <Route path="/brains" element={<BrainHub />} />
        <Route path="/brains/:agent" element={<BrainHub />} />
        <Route path="/brains/:agent/cat/:catId" element={<BrainHub />} />
        <Route path="/brains/:agent/item/:nodeId" element={<BrainHub />} />
        <Route
          path="/brains/:agent/nodes"
          element={
            <Suspense fallback={<div className="content"><p className="small">Loading the node room…</p></div>}>
              <BrainExplorer />
            </Suspense>
          }
        />
        <Route path="/knowledge" element={<Knowledge />} />
        <Route path="/reports" element={<Reports />} />
        <Route path="/how-it-works" element={<HowItWorks />} />
        <Route path="/settings" element={<Settings />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
}
